import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import SectionHeading from '@/components/feature/SectionHeading';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/feature/EmptyState';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import HeroCarousel from './components/HeroCarousel';
import CategoryShortcuts from './components/CategoryShortcuts';
import { getFeaturedMovies, getHotMovies, getLatestMovies, formatViews } from '@/utils/movie';

const STATS = [
  { value: '12,800+', label: '影视条目' },
  { value: '36', label: '细分类型' },
  { value: '每日', label: '更新推荐' },
  { value: '高清', label: '内容聚合' },
];

export default function Home() {
  const featured = getFeaturedMovies();
  const latest = getLatestMovies(10);
  const hot = getHotMovies(5);
  const hotList = getHotMovies(8);

  useDocumentMeta({
    title: '丝瓜视频-优质视频内容聚合平台',
    description:
      '丝瓜视频为您提供丰富的视频内容，包括电影、电视剧及综艺节目，方便用户在线观看与搜索最新影视资源。',
    keywords: '丝瓜视频,视频平台,影视资源,电影电视剧,在线视频,影视聚合,视频内容',
    canonical: 'https://nnxinfangxiang.cn/',
  });

  return (
    <SiteLayout>
      <HeroCarousel movies={featured} />

      <div className="mx-auto w-full max-w-[1360px] px-4 md:px-6">
        <section className="py-10 md:py-14">
          <SectionHeading title="频道快捷入口" subtitle="按类型快速进入你想看的内容" icon="ri-apps-2-line" />
          <CategoryShortcuts />
        </section>

        <section className="py-2 md:py-4">
          <SectionHeading
            title="最新更新"
            subtitle="按更新时间排序，第一时间发现新内容"
            icon="ri-time-line"
            action={
              <Link to="/category/movie" className="hidden items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 sm:flex">
                查看全部 <i className="ri-arrow-right-s-line" />
              </Link>
            }
          />
          {latest.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {latest.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <EmptyState title="暂未更新，敬请期待" description="我们正在努力补充更多影视内容，稍后再回来看看吧。" />
          )}
        </section>

        <section className="py-10 md:py-14">
          <SectionHeading title="热门推荐" subtitle="依据播放热度精选，大家正在看的内容" icon="ri-fire-line" />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:col-span-8 lg:grid-cols-3">
              {hot.map((movie, i) => (
                <MovieCard key={movie.id} movie={movie} rank={i + 1} />
              ))}
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5 lg:col-span-4">
              <div className="flex items-center gap-2">
                <i className="ri-bar-chart-2-line text-xl text-accent-600" />
                <h3 className="font-heading text-base font-bold text-foreground-950">热播榜 TOP 8</h3>
              </div>
              <ul className="mt-4 space-y-1">
                {hotList.map((movie, i) => (
                  <li key={movie.id}>
                    <Link
                      to={`/detail/${movie.id}`}
                      className="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-50"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-label text-xs font-bold ${
                          i < 3 ? 'bg-accent-500 text-background-50' : 'bg-background-300 text-foreground-700'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-1 block text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                          {movie.title}
                        </span>
                        <span className="text-xs text-foreground-400">
                          {movie.year} · {movie.region}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs text-foreground-400">{formatViews(movie.views)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-6 md:py-10">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-primary-950">
            <div className="grid grid-cols-1 items-center gap-8 p-6 md:grid-cols-2 md:p-10">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/90 px-3 py-1 font-label text-xs font-semibold text-background-50">
                  <i className="ri-leaf-line" /> 关于丝瓜视频
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-3xl">
                  汇聚优质影视，轻松发现你想看的
                </h2>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-primary-100 md:text-base">
                  丝瓜视频专注于优质影视资源聚合，覆盖电影、电视剧与综艺节目。我们通过清晰的分类、智能筛选与关键词搜索，帮助你在海量内容中快速找到心仪的影片，随时了解最新影视资讯。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-information-line" /> 了解更多
                  </Link>
                  <Link
                    to="/search"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    <i className="ri-search-line" /> 搜索影视
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {STATS.map((stat) => (
                  <div key={stat.label} className="rounded-lg bg-background-50/5 p-4 backdrop-blur md:p-5">
                    <div className="font-heading text-2xl font-extrabold text-accent-400 md:text-3xl">{stat.value}</div>
                    <div className="mt-1 text-xs text-primary-100 md:text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}